
import React from 'react';
import { Clock, ArrowRight, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { SearchResult } from '@/services/search';
import { EntityIcon } from './EntityIcon';

interface SearchDropdownProps {
  loading: boolean;
  error: string | null;
  query: string;
  results: SearchResult[];
  searchHistory: string[];
  selectedIndex: number;
  onResultClick: (result: SearchResult) => void;
  onHistoryClick: (historyQuery: string) => void;
  onShowAllResults: () => void;
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  loading,
  error,
  query,
  results,
  searchHistory,
  selectedIndex,
  onResultClick,
  onHistoryClick,
  onShowAllResults
}) => {
  const displayResults = query.trim() ? results.slice(0, 5) : [];
  const displayHistory = !query.trim() ? searchHistory.slice(0, 5) : [];

  return (
    <Card className="absolute top-full left-0 right-0 mt-1 z-50 max-h-96 overflow-y-auto">
      <CardContent className="p-0">
        {loading && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Поиск...
          </div>
        )}

        {error && (
          <div className="p-4 text-center text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Search Results */}
        {displayResults.length > 0 && (
          <div>
            <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50">
              Результаты поиска
            </div>
            {displayResults.map((result, index) => (
              <div
                key={`result-${result.id}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 border-b border-border/50",
                  selectedIndex === index && "bg-muted"
                )}
                onClick={() => onResultClick(result)}
              >
                <EntityIcon type={result.type} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm truncate">{result.title}</span>
                    <Badge variant="secondary" className="text-xs">
                      {result.type}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {result.subtitle}
                  </div>
                  {result.description && (
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {result.description}
                    </div>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
            
            {/* Show all results link */}
            {query.trim() && results.length > 5 && (
              <div
                className={cn(
                  "flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 border-t border-border/50 bg-muted/20",
                  selectedIndex === displayResults.length + displayHistory.length && "bg-muted"
                )}
                onClick={onShowAllResults}
              >
                <ExternalLink className="h-4 w-4 text-primary" />
                <span className="text-sm text-primary font-medium">
                  Показать все результаты ({results.length})
                </span>
              </div>
            )}
          </div>
        )}

        {/* Search History */}
        {displayHistory.length > 0 && (
          <div>
            <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50 border-t border-border/50">
              Недавние поиски
            </div>
            {displayHistory.map((historyQuery, index) => (
              <div
                key={`history-${index}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50",
                  selectedIndex === displayResults.length + index && "bg-muted"
                )}
                onClick={() => onHistoryClick(historyQuery)}
              >
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm truncate">{historyQuery}</span>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && !error && query.trim() && displayResults.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Ничего не найдено по запросу "{query}"
          </div>
        )}
      </CardContent>
    </Card>
  );
};
